/* eslint @typescript-eslint/no-explicit-any: 0 */
import axios, { AxiosInstance } from 'axios'
import { ILanguageResource } from '../types'
import { mapTranslationsToResource } from './helpers'
import { IPoeTranslation } from './types'
import clone from 'lodash.clonedeep'
import * as qs from 'qs'
import i18n from 'i18next'

type PoeLoaderSubscriberType = () => void

// Client side resource loader that pulls live translations from POEditor
export default class PoeLoader {
	private http: AxiosInstance
	private status: 'idle' | 'loading' | 'loaded' = 'idle'
	private subscribers: PoeLoaderSubscriberType[] = []

	constructor(private languageCodes: string[], private defaultResources: Record<string, ILanguageResource>) {
		this.http = axios.create({
			baseURL: `${import.meta.env.VITE_DATAHUB_API_ENDPOINT}/Poe`,
		})
	}

	private async getTranslations(languageCode: string): Promise<IPoeTranslation[]> {
		const response = await this.http.get(
			`?${qs.stringify({
				api_token: import.meta.env.VITE_POE_READ_TOKEN as string,
				id: import.meta.env.VITE_POE_PROJECT_ID as string,
				language: languageCode,
			})}`
		)
		return response?.data.map(
			({ term, context, translation }: any): IPoeTranslation => ({
				key: term,
				context,
				content: translation.content,
			})
		)
	}

	private async getResources() {
		const translations = await Promise.all(this.languageCodes.map(code => this.getTranslations(code)))
		return this.languageCodes.reduce((acc, code, index) => {
			acc[code] = mapTranslationsToResource(translations[index], clone(this.defaultResources[code]))
			return acc
		}, {} as Record<string, ILanguageResource>)
	}

	subscribe(subscriber: PoeLoaderSubscriberType): () => void {
		this.subscribers.push(subscriber)
		if (this.status === 'idle') {
			this.status = 'loading'
			this.getResources()
				.then(resources => {
					Object.keys(resources).forEach(code => {
						i18n.addResourceBundle(code, 'translation', resources[code].translation)
					})

					this.status = 'loaded'
					this.subscribers.forEach(sub => sub())
				})
				.catch(err => {
					console.warn(`Could not load POEditor languages: ${err.message}`)
				})
		}

		return () => {
			const index = this.subscribers.indexOf(subscriber)
			if (index !== -1) {
				this.subscribers.splice(index, 1)
			}
		}
	}
}
